/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../constants/ServiceConstants';
import utils from '../../../../common/utils';
import parsePublishResponse from '../../utils/PublishResponseUtils';

/**
 * CreateVersionService - Handles creating a new version of an existing graph
 * API: POST /graphs/:graphId/versions
 *
 * Used when:
 * - Saving changes on a LIVE graph (creates new DRAFT version)
 * - Publishing from LIVE graph (creates new LIVE version when type: GRAPH_OPERATION.PUBLISH is passed)
 */
export default class CreateVersionService extends BaseService {
  constructor() {
    super(constants.CREATE_GRAPH_VERSION, 'createVersionStatus');
  }

  getErrorMessage(data, fallback) {
    return data?.error || data?.message || fallback;
  }

  async handleEvent(eventName, event) {
    const { payload, headers } = utils.buildRequestPartsFromPayload(event);
    const {
      graphId,
      graphName,
      comment,
      isDefault,
      defaultVersionVal,
      type,
      graphData,
      targetEnv,
      targetTenantId,
      targetTenantName,
      targetTenantTitle,
      version
    } = payload;

    this.targetTenantTitle = targetTenantTitle;
    this.suppressSuccessNotification = Boolean(payload.suppressSuccessNotification);

    const requestData = {
      graphName,
      comment,
      isDefault,
      defaultVersionVal,
      graphData,
      ...(type && { type }),
      ...(targetEnv && { targetEnv }),
      ...(targetTenantId && { targetTenantId }),
      ...(targetTenantName && { targetTenantName }),
      ...(version != null && { version })
    };

    const { isStackB } = utils.getStackBParams();

    if (isStackB) {
      const enhancedHeaders = await utils.resolveStackBHeaders(headers);
      return this.getClient('aiadminstackb').post(`/graphs/${graphId}/versions`, requestData, {
        headers: enhancedHeaders
      });
    }
    return this.getClient('aiadmin').post(`/graphs/${graphId}/versions`, requestData, { headers });
  }

  handleResponse(response) {
    const { data } = response;

    if (data && data.success) {
      const { message, langMessage, type } = parsePublishResponse(
        data,
        'publishResponse.NEW_VERSION_CREATED_SUCCESSFULLY',
        'create',
        { targetTenantTitle: this.targetTenantTitle }
      );

      if (!this.suppressSuccessNotification) {
        const notification = {
          open: true,
          type,
          message,
          langMessage
        };
        Framework.getStoreRegistry().getStore('notificationState').set(notification);
      }
      const graphModel = Framework.getStoreRegistry().getStore('graphModel');
      graphModel.clearError();
      Framework.getEventManager().publish(constants.CREATE_GRAPH_VERSION_SUCCESS, { data });
    } else {
      const msg = this.getErrorMessage(data, 'Unknown error');
      const notification = {
        open: true,
        type: 'error',
        message: `Failed to create version: ${msg}`
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
      Framework.getStoreRegistry().getStore('graphModel').setError(msg);
      Framework.getEventManager().publish(constants.CREATE_GRAPH_VERSION_ERROR);
    }
  }

  handleError(error) {
    const { response } = error;

    if (response) {
      const { status, data } = response;
      if (status === 401) {
        utils.redirectToLogin();
      }
      const msg = this.getErrorMessage(data, 'Failed to create version. Please try again.');
      const notification = {
        open: true,
        message: msg,
        type: 'error'
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
      Framework.getStoreRegistry().getStore('graphModel').setError(msg);
    } else if (error.message === 'timeout of 60000ms exceeded') {
      const notification = {
        open: true,
        message: 'Request timeout. Please try again.',
        type: 'error'
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
      Framework.getStoreRegistry().getStore('graphModel').setError('Request timeout. Please try again.');
    }
    Framework.getEventManager().publish(constants.CREATE_GRAPH_VERSION_ERROR);
  }
}
